/// <amd-module name="Feed/Legacy/Protocols/PyPi/PyPiConnectToFeedPanel" />
import * as React from "react";

import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";

import { FormatComponent } from "VSS/Platform/Components/Format";

import { CiConstants, FwLinks } from "Feed/Common/Constants/Constants";
import { FeedView } from "Feed/Common/Contracts/Feed";
import * as Resources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { LabelWithExternalLink } from "Feed/Legacy/Components/LabelWithExternalLink";
import { PackageMessagePanel } from "Feed/Legacy/Components/PackageMessagePanel";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { isUserAnonymousOrPublic } from "Feed/Legacy/Helpers/PermissionHelper";
import { IConnectToFeedProps } from "Feed/Legacy/Protocols/Common/IConnectToFeedProps";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { PyPiCiConstants } from "Feed/Legacy/Protocols/PyPi/PyPiConstants";
import { IPyPiRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/PyPi/PyPiRestApiUrlGeneratorService";
import { HubAction } from "Feed/Legacy/Types/IHubState";

export interface IPyPiConnectToFeedPanelProps extends IConnectToFeedProps {
    getPublishCommand: (feedName: string) => string;
    feedViews: FeedView[];
}

export interface IPyPiConnectToFeedPanelState {
    isLoading: boolean;
    simpleEndpointUrl?: string;
    uploadEndpointUrl?: string;
    authTokenInstallCommand?: string;
    authTokenPublishCommand?: string;
    isInstallAuthTokenLoading?: boolean;
    isPublishAuthTokenLoading?: boolean;
}

export class PyPiConnectToFeedPanel extends React.Component<
    IPyPiConnectToFeedPanelProps,
    IPyPiConnectToFeedPanelState
> {
    private publishGeneratedCredentialsElement: CopyableTextField;
    private installGeneratedCredentialsElement: CopyableTextField;
    private showMemberDetails: boolean;

    private textfieldIds = [
        "pypi-connect-to-feed-textfield-pypiIniSubheading",
        "pypi-connect-to-feed-textfield-pypiRcSubheading",
        "pypi-connect-to-feed-textfield-createPackage",
        "pypi-connect-to-feed-textfield-uploadPackage"
    ];

    constructor(props: IPyPiConnectToFeedPanelProps) {
        super(props);

        const { feed } = this.props;

        const includeProject = feed && feed.project != null;
        const simpleEndpointUrl = this.PyPiRestApiUrlGeneratorService.getSimplePackageNameUrl(
            feed.name,
            includeProject,
            undefined
        );
        const uploadEndpointUrl = this.PyPiRestApiUrlGeneratorService.getPackageUploadUrl(feed.name, includeProject);
        this.showMemberDetails = isUserAnonymousOrPublic(this.props.pageContext, this.props.feed) == false;

        this.state = {
            isLoading: false,
            simpleEndpointUrl,
            uploadEndpointUrl
        };

        CustomerIntelligenceHelper.publishEvent(this.props.pageContext, CiConstants.ConnectToFeed, {
            Action: CiConstants.ConnectToFeedTabChanged,
            Protocol: PyPiCiConstants.ProtocolName,
            Context: HubAction[this.props.hubAction]
        });
    }

    public render(): JSX.Element {
        return (
            <div>
                {this.state.simpleEndpointUrl ? (
                    <div className="pypi-connect-to-feed-panel connect-to-feed-panel">
                        <div className="connect-to-feed-section">
                            <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                                {Resources.PyPiConnectControl_InstallHeadingText}
                            </div>
                            <div className="connect-to-feed-sub-heading">
                                <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                                    {Resources.PyPiConnectControl_VirtualEnvTitle}
                                </span>
                                <div>
                                    <FormatComponent format={Resources.PyPiConnectControl_VirtualEnvText}>
                                        <ExternalLink
                                            pageContext={this.props.pageContext}
                                            href={FwLinks.CreateVirtualEnvPyPi}
                                        >
                                            {Resources.PyPiConnectControl_VirtualEnvLinkText}
                                        </ExternalLink>
                                    </FormatComponent>
                                </div>
                            </div>
                            {this.showMemberDetails && this.renderGenerateCredentials()}
                            <div className="connect-to-feed-sub-heading">
                                <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                                    {Resources.PyPiConnectControl_SetUpInstallPackageTitle}
                                </span>
                                <div>
                                    <FormatComponent format={Resources.PyPiConnectControl_SetUpInstallPackageText}>
                                        <strong>{Resources.GetThisPackage}</strong>
                                    </FormatComponent>
                                </div>
                            </div>
                        </div>
                        {this.showMemberDetails && this.renderPublishSteps()}
                    </div>
                ) : (
                    <PackageMessagePanel message={Resources.ConnectControl_EndpointNotAvailable} />
                )}
            </div>
        );
    }

    private renderGenerateCredentials() {
        const viewOptions: IDropdownOption[] = [];
        viewOptions.push({
            key: Resources.FeedViewDropdown_AllViews_Key,
            text: Resources.FeedViewDropdown_ConnectToFeed_AllViews_Name
        } as IDropdownOption);

        if (this.props.feedViews) {
            this.props.feedViews.forEach(view => {
                viewOptions.push({
                    key: view.id,
                    text: "@" + view.name
                } as IDropdownOption);
            });
        }

        return (
            <div>
                <div className="connect-to-feed-sub-heading">
                    <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                        {Resources.PyPiConnectControl_ProtectCredsTitle}
                    </span>
                    <div>
                        <FormatComponent format={Resources.PyPiConnectControl_ProtectCredsText}>
                            <span className="code-text">{"eng"}</span>
                            <span className="code-text">{"eng/"}</span>
                        </FormatComponent>
                    </div>
                </div>
                <div className="connect-to-feed-sub-heading">
                    <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                        {Resources.PyPiConnectControl_SetUpPipIniTitle}
                    </span>
                    <div>
                        <FormatComponent format={Resources.PyPiConnectControl_SetUpPipIniText}>
                            <span className="code-text">{Resources.PipIniFile}</span>
                            <span className="code-text">{Resources.PipConfFile}</span>
                            <strong>{Resources.PyPiConnectControl_GenerateCredentialsText}</strong>
                        </FormatComponent>
                    </div>
                </div>
                <div className="connect-to-feed-views-dropdown">
                    <LabelWithExternalLink
                        pageContext={this.props.pageContext}
                        labelText={Resources.FeedViewDropdown_ConnectToFeed_Label}
                        linkText={Resources.FeedViewDropdown_ConnectToFeed_Label_Link}
                        href={FwLinks.ConnectToFeedViewWhatsThis}
                        ciContext={PyPiCiConstants.PyPiConnectToFeedPanel}
                    />
                    <Dropdown
                        className="views-dropdown"
                        ariaLabel={Resources.FeedViewDropdown_ConnectToFeed_Label}
                        options={viewOptions}
                        onChanged={option => this.onViewDropdownChanged(option)}
                        defaultSelectedKey={
                            this.props.feed && this.props.feed.view
                                ? this.props.feed.view.id
                                : Resources.FeedViewDropdown_AllViews_Key
                        }
                    />
                </div>
                <div>Credential generation has been removed from the legacy CtF UX.</div>
            </div>
        );
    }

    private renderPublishSteps() {
        const publishCommand = this.getPublishCommand();

        return (
            <div>
                <div className="connect-to-feed-section">
                    <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                        {Resources.PyPiConnectControl_UploadHeadingText}
                    </div>
                    <div className="connect-to-feed-sub-heading">
                        <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                            {Resources.PyPiConnectControl_SetUpPypircTitle}
                        </span>
                        <div>
                            <FormatComponent format={Resources.PyPiConnectControl_SetUpPypircText}>
                                <span className="code-text">{Resources.PypiRcFile}</span>
                            </FormatComponent>
                        </div>
                    </div>
                    <div className="connect-to-feed-textfield-container">
                        <div className="connect-to-feed-sub-heading" id={this.textfieldIds[1]}>
                            {Resources.PyPiConnectControl_PyPiRcSubHeading}
                        </div>
                        <CopyableTextField
                            pageContext={this.props.pageContext}
                            ref={self => (this.publishGeneratedCredentialsElement = self)}
                            text={this.state.authTokenPublishCommand}
                            buttonAriaLabel={Resources.AriaLabel_CopyToClipBoard_PyPi_ManualCredentials_Copy}
                            textFieldAriaLabel={Resources.AriaLabel_CopyToClipBoard_PyPi_ManualCredentials}
                            telemetryProperties={
                                {
                                    commandName: CiConstants.CopyCommandAuthToken,
                                    feedName: this.props.feed.name,
                                    protocol: PyPiCiConstants.ProtocolName
                                } as ICopyableTextFieldTelemetry
                            }
                            ariaDescribedByIdForLabelText={this.textfieldIds[1]}
                            cssClassToOverWrite={"pre-wrap-pypi"}
                        />
                    </div>
                    <div className="connect-to-feed-sub-heading">
                        <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                            {Resources.PyPiConnectControl_AvoidPublishTitle}
                        </span>
                        <div>
                            <FormatComponent format={Resources.PyPiConnectControl_AvoidPublishText}>
                                <span className="code-text">{Resources.PypiRcFile}</span>
                                <span className="code-text">{"[pypi]"}</span>
                                <span className="code-text">{Resources.PypiRcFile}</span>
                            </FormatComponent>
                        </div>
                    </div>
                    <div className="connect-to-feed-sub-heading" id={this.textfieldIds[2]}>
                        <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                            {Resources.PyPiConnectControl_CreatePackageTitle}
                        </span>
                        <div>
                            <FormatComponent format={Resources.PyPiConnectControl_CreatePackageText}>
                                <ExternalLink
                                    pageContext={this.props.pageContext}
                                    href={FwLinks.SupportedDistTypesPyPi}
                                >
                                    {Resources.PyPiConnectControl_CreatePackageLinkText}
                                </ExternalLink>
                                <span className="code-text">{Resources.SetUpPyFile}</span>
                            </FormatComponent>
                        </div>
                    </div>
                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        text={Resources.PyPiConnectControl_CreatePackageCommandText}
                        buttonAriaLabel={Resources.AriaLabel_CopyToClipBoard_CreatePackage_Copy}
                        textFieldAriaLabel={Resources.AriaLabel_CopyToClipBoard_CreatePackage}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandPush,
                                feedName: this.props.feed.name,
                                protocol: PyPiCiConstants.ProtocolName
                            } as ICopyableTextFieldTelemetry
                        }
                        ariaDescribedByIdForLabelText={this.textfieldIds[2]}
                    />
                    <div className="connect-to-feed-sub-heading" id={this.textfieldIds[3]}>
                        <span className="connect-to-feed-sub-heading-bold" role="heading" aria-level={3}>
                            {Resources.PyPiConnectControl_UploadPackageTitle}
                        </span>
                        <div>
                            <FormatComponent format={Resources.PyPiConnectControl_UploadPackageText}>
                                <strong>{Resources.Important}</strong>
                                <span className="code-text">{Resources.PyPiConnectControl_FeedNameHere}</span>
                            </FormatComponent>
                        </div>
                    </div>
                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        text={publishCommand}
                        buttonAriaLabel={Resources.AriaLabel_CopyToClipBoard_Publish_Copy}
                        textFieldAriaLabel={Resources.AriaLabel_CopyToClipBoard_Publish}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandPush,
                                feedName: this.props.feed.name,
                                protocol: PyPiCiConstants.ProtocolName
                            } as ICopyableTextFieldTelemetry
                        }
                        ariaDescribedByIdForLabelText={this.textfieldIds[3]}
                    />
                </div>
            </div>
        );
    }

    private getPublishCommand(): string {
        return this.props.getPublishCommand(this.props.feed.name);
    }

    private get PyPiRestApiUrlGeneratorService(): IPyPiRestApiUrlGeneratorService {
        return this.props.pageContext.getService<IPyPiRestApiUrlGeneratorService>("IPyPiRestApiUrlGeneratorService");
    }

    private onViewDropdownChanged(option: IDropdownOption): void {
        let feed = this.props.feed;
        let feedName = feed.name;

        if (option.key !== Resources.FeedViewDropdown_AllViews_Key) {
            feedName = feedName + option.text;
        }

        const includeProject = feed && feed.project != null;
        const simpleEndpointUrl = this.PyPiRestApiUrlGeneratorService.getSimplePackageNameUrl(
            feedName,
            includeProject,
            undefined
        );

        this.setState({
            simpleEndpointUrl: simpleEndpointUrl,
            authTokenInstallCommand: null
        });
    }
}
